// App.tsx
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Container } from "@mui/material";
import "bootstrap/dist/css/bootstrap.min.css";

// Pages
import Home from "./pages/Home";
import User from "./pages/User";
import Company from "./pages/Company";
import System from "./pages/System";
import Feature from "./pages/Feature";
import NotFound from "./pages/NotFound";
import About from "./pages/About";
import SignIn from "./pages/SignIn";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

function RequireAuth({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, isAuthenticating } = useAuth();

    if (isAuthenticating) {
        return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
        return <Navigate to="/" />;
    }

    return <>{children}</>;
}

function AppRoutes() {
    const { isAuthenticated } = useAuth();

    return (
        <Routes>
            {/* Auth Routes */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={isAuthenticated ? <Navigate to="/home" /> : <SignIn />} />

            {/* Protected Routes */}
            <Route
                path="/home"
                element={
                    <RequireAuth>
                        <Home />
                    </RequireAuth>
                }
            />
            <Route
                path="/user/:userId"
                element={
                    <RequireAuth>
                        <User />
                    </RequireAuth>
                }
            />
            <Route
                path="/company/:companyId"
                element={
                    <RequireAuth>
                        <Company />
                    </RequireAuth>
                }
            />
            <Route
                path="/system/:systemId"
                element={
                    <RequireAuth>
                        <System />
                    </RequireAuth>
                }
            />
            <Route
                path="/feature/:featureId"
                element={
                    <RequireAuth>
                        <Feature />
                    </RequireAuth>
                }
            />
            <Route
                path="/about"
                element={
                    <RequireAuth>
                        <About />
                    </RequireAuth>
                }
            />

            {/* Catch all routes */}
            <Route path="/lost" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}

const queryClient = new QueryClient();
function App() {
    return (
        <Container disableGutters maxWidth={false}>
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <AppRoutes />
                </AuthProvider>
                <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
        </Container>
    );
}

export default App;
